import { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Box, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getCoinbaseBars, type CoinbaseBarSeries } from '../../services/coinbaseService';

interface Props {
  productId: string;
  mid?: number;
}

interface ChartPoint {
  time: number;
  label: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

function formatLabel(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatPrice(n: number): string {
  return n >= 1000
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : `$${n.toFixed(2)}`;
}

export default function CoinbasePriceChart({ productId, mid }: Props) {
  const theme = useTheme();
  const [series, setSeries] = useState<CoinbaseBarSeries[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCoinbaseBars(productId)
      .then(data => { if (!cancelled) setSeries(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [productId]);

  const fiveMin = series.find(s => s.timeframe === 'FIVE_MINUTE');
  const candles = fiveMin?.candles ?? [];

  if (error || candles.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          {error ? 'Chart unavailable' : 'Waiting for bar data…'}
        </Typography>
      </Box>
    );
  }

  const points: ChartPoint[] = candles.slice(-80).map(c => ({
    time: new Date(c.time).getTime(),
    label: formatLabel(new Date(c.time).getTime()),
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume,
  }));

  const prices = points.flatMap(p => [p.high, p.low]);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const pad = (maxPrice - minPrice) * 0.05;
  const lastClose = points[points.length - 1]?.close;
  const firstClose = points[0]?.close;
  const pctChange = firstClose && lastClose ? ((lastClose - firstClose) / firstClose) * 100 : 0;
  const trendColor = pctChange >= 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>{productId}</Typography>
        {mid !== undefined && (
          <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', color: trendColor }}>
            {formatPrice(mid)}
          </Typography>
        )}
        <Chip
          size="small"
          label={`${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(2)}% (80 bars)`}
          color={pctChange >= 0 ? 'success' : 'error'}
          variant="outlined"
          sx={{ fontSize: '0.65rem' }}
        />
        <Typography variant="caption" color="text.disabled">5-min · {candles.length} candles</Typography>
      </Box>

      {/* Price chart */}
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={points} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`grad-${productId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={trendColor} stopOpacity={0.25} />
              <stop offset="95%" stopColor={trendColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: theme.palette.text.disabled, fontSize: 9 }}
            interval={Math.floor(points.length / 6)}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[minPrice - pad, maxPrice + pad]}
            tick={{ fill: theme.palette.text.disabled, fontSize: 9 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={v => formatPrice(v as number)}
            width={60}
          />
          <Tooltip
            contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 4, fontSize: 11 }}
            formatter={(v: unknown) => [formatPrice(v as number)]}
            labelStyle={{ color: theme.palette.text.secondary, fontSize: 10 }}
          />
          <Area
            type="monotone"
            dataKey="close"
            stroke={trendColor}
            strokeWidth={1.5}
            fill={`url(#grad-${productId})`}
            dot={false}
            activeDot={{ r: 3 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Volume chart */}
      <ResponsiveContainer width="100%" height={40}>
        <BarChart data={points} margin={{ top: 0, right: 4, left: 0, bottom: 0 }}>
          <YAxis hide domain={[0, 'auto']} />
          <Bar dataKey="volume" fill={theme.palette.primary.main} opacity={0.4} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
